import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import Layout from '@/components/Layout/Layout';
import AddVariant from '@/components/product/AddVariant';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import { useDebounce } from '@/hooks/useDebounce';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { ActionConfig, PageInfo } from '@/interfaces/common.interface';
import { IVariantData, IVariantList } from '@/interfaces/VariantList.interface';
import {
  formatDateTime,
  GetFilterUrl,
  showToastNotification,
} from '@/utility/helper';
import { useEffect, useMemo, useState } from 'react';

interface StatesProp {
  showAddModel: boolean;
  variants: IVariantData[];
  pageInfo: PageInfo;
  isLoading: boolean;
  showDeleteModel: boolean;
  selectedVariant: IVariantData | null;
}

const Variants = () => {
  const { canView, canEdit, canDelete } = usePermissions();
  const [searchValue, setSearchValue] = useState('');
  const debounceValue = useDebounce(searchValue);
  const [states, setStates] = useState<StatesProp>({
    showAddModel: false,
    variants: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
    isLoading: false,
    showDeleteModel: false,
    selectedVariant: null,
  });

  const handleChangeState = (newState: Partial<StatesProp>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.variants);

  const fetchVariants = async () => {
    try {
      handleChangeState({ isLoading: true });
      const filterQuery = {
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        search: debounceValue,
      };
      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.variants}?${query ? query : ''}`;
      const res = await callApi<IVariantList>(PATH, { method: 'GET' });
      handleChangeState({
        variants: res.data,
        pageInfo: {
          limit: res.pageInfo.limit,
          page: res.pageInfo.page,
          total: res.pageInfo.total,
          totalPages: res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      ThrowError(error);
      handleChangeState({ isLoading: false });
    }
  };

  const handleDeleteVariant = async (id: string) => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.variants}/${id}`;
      await callApi(PATH, { method: 'DELETE' });
      handleChangeState({
        pageInfo: {
          ...states.pageInfo,
          page: 1,
        },
      });
      fetchVariants();
      showToastNotification('success', 'Variant deleted successfully');
    } catch (error: unknown) {
      ThrowError(error);
    } finally {
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchVariants();
  }, [states.pageInfo.page, states.pageInfo.limit, debounceValue]);

  const columns: TableColumn<IVariantData>[] = [
    {
      key: 'name',
      header: 'Name',
    },
    {
      key: 'productCount',
      header: 'Products',
    },
    {
      key: 'createdAt',
      header: 'Created At',
      render: (item) => {
        return <span>{formatDateTime(item.createdAt, 'YYMMMDDHH')}</span>;
      },
    },
    {
      key: 'updatedAt',
      header: 'Last Update At',
      render: (item) => {
        return <span>{formatDateTime(item.updatedAt, 'YYMMMDDHH')}</span>;
      },
    },
  ];

  const productActionConfig: Record<string, ActionConfig<IVariantData>> = {
    // view: {
    //     text: 'View',
    //     onClick: (variant) => { },
    //     permission: () => canView('product'),
    // },
    eidt: {
      text: 'Edit',
      onClick: (variant) => {
        handleChangeState({ showAddModel: true, selectedVariant: variant });
      },
      permission: () => canEdit('product'),
    },
    delete: {
      text: 'Delete',
      onClick: (variant) => {
        handleChangeState({ showDeleteModel: true, selectedVariant: variant });
      },
      permission: () => canDelete('product'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(productActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canView, canEdit, canDelete],
  );

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    });
  };

  const handleChangePageSize = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page: 1,
        limit,
      },
    });
  };

  return (
    <Layout>
      <PageHeading
        text="Variants"
        button={{
          text: 'Add Variant',
          onClick: () => handleChangeState({ showAddModel: true }),
        }}
      />
      {states.showAddModel && (
        <AddVariant
          show={true}
          onSuccess={() => fetchVariants()}
          modelType={states.selectedVariant?.id ? 'update' : 'add'}
          variantName={states.selectedVariant?.name}
          variantId={states.selectedVariant?.id}
          onClose={() =>
            handleChangeState({ showAddModel: false, selectedVariant: null })
          }
        />
      )}

      <Card>
        <div className="mb-6">
          <FormField
            placeholder="Search for variants..."
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
        <CommonTable
          columns={columns}
          data={states.variants}
          showCheckbox={false}
          getItemId={(item) => item.id}
          emptyMessage="No products found"
          loading={states.isLoading}
          minHeight="min-h-44"
          actions={actions}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
        />
        <Pagination
          isLoading={states.isLoading}
          paginationData={states.pageInfo}
          onPageChange={(page) => handleChangePage(page)}
          onPageSizeChange={(limit) => handleChangePageSize(limit)}
        />
      </Card>
      {states.showDeleteModel && (
        <ConfirmationModel
          show={true}
          onClose={() => {
            handleChangeState({
              showDeleteModel: false,
              selectedVariant: null,
            });
          }}
          type="variant"
          onConfirm={() =>
            handleDeleteVariant(states.selectedVariant?.id as string)
          }
        />
      )}
    </Layout>
  );
};

export default Variants;
